import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calculator as CalcIcon, ArrowRight, Info, ShieldCheck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { INDICATIVE_RATE, INDICATIVE_RATE_PCT, weeklyRepayment } from '../lib/finance';
import { unsplash } from '../lib/images';
import CalculatorGuide from './calculator/CalculatorGuide';

// Unsplash, matching the pattern already used for the landing page's imagery — swap for a real
// Levr photo when there is one. Chosen after looking at the alternatives: no number plate, no
// people, and an everyday car rather than the supercar or the US-plated truck in a Nevada desert
// that the obvious search results turn up.
const CAR_IMAGE = unsplash('photo-1502877338535-766e1452684a', { w: 1400 });

const Calculator = () => {
  const [vehiclePrice, setVehiclePrice] = useState(35000);
  const [deposit, setDeposit] = useState(5000);
  const [termMonths, setTermMonths] = useState(60);
  const navigate = useNavigate();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const loanAmount = vehiclePrice - deposit;
  // Computed here from the site-wide indicative rate. This page used to display named lenders and
  // their quotes, pulled from the seeded lender table — companies Levr does not have on panel.
  // Showing a rate attributed to a lender we can't place business with is a promise we can't keep.
  const estimatedWeekly = weeklyRepayment(loanAmount, termMonths, INDICATIVE_RATE);

  const handleApply = () => {
    navigate('/get-started');
  };

  const termOptions = [
    { value: 12, label: '12 months' },
    { value: 24, label: '24 months' },
    { value: 36, label: '36 months' },
    { value: 48, label: '48 months' },
    { value: 60, label: '60 months' },
    { value: 72, label: '72 months' },
    { value: 84, label: '84 months' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-8 md:pt-32 md:pb-12 bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-[#00D47E]/20 text-[#00D47E] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <CalcIcon className="w-4 h-4" />
              Loan Calculator
            </div>
            <h1 className="font-outfit text-3xl md:text-5xl font-bold text-white mb-4">
              Calculate Your Finance
            </h1>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              Move the sliders to see what your weekly repayments could look like
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculator Section */}
      <section data-testid="calculator-section" className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Calculator Panel */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 sticky top-24"
              >
                <h2 className="font-outfit text-2xl font-bold text-black mb-8">
                  Your Details
                </h2>

                {/* Vehicle Price */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      Vehicle Price
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="w-4 h-4 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>The total purchase price of the vehicle</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </label>
                    <span className="font-outfit text-xl font-bold text-black">
                      {formatCurrency(vehiclePrice)}
                    </span>
                  </div>
                  <Slider
                    data-testid="vehicle-price-slider"
                    value={[vehiclePrice]}
                    onValueChange={(val) => setVehiclePrice(val[0])}
                    min={5000}
                    max={150000}
                    step={1000}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-2">
                    <span>$5,000</span>
                    <span>$150,000</span>
                  </div>
                </div>

                {/* Deposit */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      Deposit
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="w-4 h-4 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Your upfront payment (min 10% recommended)</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </label>
                    <span className="font-outfit text-xl font-bold text-black">
                      {formatCurrency(deposit)}
                    </span>
                  </div>
                  <Slider
                    data-testid="deposit-slider"
                    value={[deposit]}
                    onValueChange={(val) => setDeposit(Math.min(val[0], vehiclePrice - 1000))}
                    min={0}
                    max={vehiclePrice * 0.5}
                    step={500}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-2">
                    <span>$0</span>
                    <span>{formatCurrency(vehiclePrice * 0.5)}</span>
                  </div>
                </div>

                {/* Loan Term */}
                <div className="mb-8">
                  <label className="text-sm font-medium text-gray-700 block mb-3">
                    Loan Term
                  </label>
                  <Select 
                    value={termMonths.toString()} 
                    onValueChange={(val) => setTermMonths(parseInt(val))}
                  >
                    <SelectTrigger data-testid="term-select" className="w-full h-12">
                      <SelectValue placeholder="Select term" />
                    </SelectTrigger>
                    <SelectContent>
                      {termOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value.toString()}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Loan Summary */}
                <div className="bg-white rounded-xl p-6 border border-gray-100">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-gray-600">Loan Amount</span>
                    <span className="font-outfit text-2xl font-bold text-black">
                      {formatCurrency(loanAmount)}
                    </span>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600 text-sm">Estimated Weekly</span>
                      <span className="font-outfit text-3xl font-bold text-[#00D47E]" data-testid="calc-weekly">
                        {formatCurrency(estimatedWeekly)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 text-right">
                      Based on {INDICATIVE_RATE_PCT}% over {termMonths} months
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right-hand panel. This was a list of named lenders and their quotes, built from
                the seeded lender table — companies Levr has no panel agreement with. Rather than
                dress the same data up differently, the whole thing is gone: what remains is the
                calculator's own figure and a route into the funnel that does produce a real
                answer. */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
                  <img
                    src={CAR_IMAGE}
                    alt="A blue coupe parked on a city street"
                    loading="lazy"
                    data-testid="calculator-image"
                    className="h-64 w-full object-cover md:h-80"
                  />
                  <div className="p-8">
                    <h2 className="font-outfit text-2xl font-bold text-black">
                      Want to know what you&apos;d actually be approved for?
                    </h2>
                    <p className="mt-3 text-gray-600">
                      The figure on the left is a guide based on an indicative rate. Answer a few
                      questions about your situation and we&apos;ll come back with a likelihood — no
                      credit check, so it leaves no mark on your file.
                    </p>
                    <div className="mt-5 flex items-center gap-2 text-sm text-gray-500">
                      <ShieldCheck className="h-4 w-4 text-[#00D47E]" />
                      Takes about two minutes
                    </div>
                    <Button
                      data-testid="calculator-cta-btn"
                      onClick={handleApply}
                      className="mt-6 w-full rounded-full bg-[#00D47E] py-6 text-base font-semibold text-black hover:bg-[#00D47E]/90"
                    >
                      Check my chances
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="mt-8 p-4 bg-gray-100 rounded-lg text-sm text-gray-600">
                  <p>
                    <strong>Disclaimer:</strong> These are indicative rates only. Final rates depend on your credit history, 
                    income verification, and lender approval. Our team will contact you to confirm your personalised rate.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* The substance. A slider is table stakes on this SERP — four dealership and manufacturer
          widgets already rank for it. What the page has to add is an honest reading of the number,
          which is what earns a link and what a language model can quote. See the component. */}
      <CalculatorGuide />

      <Footer />
    </div>
  );
};

export default Calculator;
